/**
 * MTA 业务技能定义
 * 
 * 将 20+ 独立工具收拢为 ~13 个语义化 skill，
 * 每个 skill 包含自描述信息和 handler 引用。
 * 
 * 注意：知识图谱（memory）和顺序思考（sequentialThinking）
 * 不纳入此处，保持为独立工具。
 * 
 * @module tools/skillDefinitions
 */

import type { SkillDefinition, MCPContent } from '../core/skillRegistry.js';

// 工具函数导入
import { analyzeProject } from './analyzeProject.js';
import { autoSetup } from './autoSetup.js';
import { initProject } from './initProject.js';
import { healthCheck } from './healthCheck.js';
import { matchAgents } from './matchAgents.js';
import { listAvailableAgents } from './listAvailableAgents.js';
import { generateConfig } from './generateConfig.js';
import { getCompactStandards } from './getCompactStandards.js';
import { getStandardById } from './getStandardById.js';
import { usePreset, listPresets } from './usePreset.js';
import { queryMappings, listScenarios } from './queryMappings.js';
import { listTemplates } from './listTemplates.js';
import { getTemplate } from './getTemplate.js';
import { analyzeReferenceProject, generateProjectSkeleton } from './cloneProject.js';
import { queryTroubleshootingCases, getTroubleshootingCaseContent, listTroubleshootingCases } from './queryTroubleshootingCases.js';
import { sketchMeasure } from './sketchMeasure.js';
import { validateRestoration } from './validateRestoration.js';
import { listWorkflows, getWorkflowDetail, recommendWorkflow, advanceWorkflow, validateWorkflow } from '../core/workflowCatalog.js';

/**
 * 通用响应包装
 */
function wrapAsContent<T>(data: T): MCPContent {
  return {
    content: [{
      type: 'text',
      text: JSON.stringify(data, null, 2),
    }],
  };
}

function extractTroubleshootingKeywords(problemText: string): string[] {
  const extractedKeywords: string[] = [];
  const keywordPatterns = [
    /\b(shadow|阴影|透出|transparency|clip|裁剪|layout|布局|spacing|间距|offset|偏移|animation|动画|color|颜色|colorfilter|fill-opacity|opacity|alpha|border|边框|svg|svgpicture|bitmap|png|icon|图标|chevron|arrow|箭头|input|输入|button|按钮|table|表格|form|表单|style|样式|viewbox|view box|design|设计稿|restore|restoration|还原)\b/gi,
    /(发灰|发浅|模糊|颜色不对|旧资源|复用|不居中|不对齐|尺寸不对|图标过大|图标过粗)/g,
  ];

  for (const pattern of keywordPatterns) {
    const matches = problemText.match(pattern);
    if (matches) {
      extractedKeywords.push(...matches.map(m => m.toLowerCase()));
    }
  }

  return [...new Set(extractedKeywords)];
}

/**
 * 所有 MTA 业务 skill 定义
 */
export const skillDefinitions: SkillDefinition[] = [

  // ==================== 规范获取类 ====================

  {
    name: 'list_workflows',
    category: 'workflow',
    description: '列出 npm 包内置的 workflow / subagent playbook，查看每条流程擅长什么任务和对应 skill 链路',
    paramHints: [
      { name: 'framework', type: 'string', description: '按框架筛选，如 flutter/vue3/react' },
      { name: 'category', type: 'string', description: '按流程类别筛选，如 delivery/diagnosis/setup/planning' },
    ],
    handler: async (params) => {
      return wrapAsContent(listWorkflows({
        framework: params.framework as string | undefined,
        category: params.category as 'delivery' | 'diagnosis' | 'setup' | 'planning' | undefined,
      }));
    },
  },

  {
    name: 'recommend_workflow',
    category: 'workflow',
    description: '按 Codex subagent 思路为当前任务推荐最合适的 workflow，返回匹配原因、下一步 skill 链路和检查点',
    paramHints: [
      { name: 'task', type: 'string', required: true, description: '当前要解决的任务描述或用户原话' },
      { name: 'framework', type: 'string', description: '显式框架，如 flutter/vue3/react' },
      { name: 'currentFile', type: 'string', description: '当前文件路径，用于辅助判断框架和场景' },
      { name: 'limit', type: 'number', description: '最多返回几条推荐 workflow' },
    ],
    handler: async (params) => {
      const task = params.task as string;
      if (!task) {
        throw new Error('recommend_workflow 需要 task 参数');
      }
      return wrapAsContent(recommendWorkflow({
        task,
        framework: params.framework as string | undefined,
        currentFile: params.currentFile as string | undefined,
        limit: params.limit as number | undefined,
      }));
    },
  },

  {
    name: 'get_workflow',
    category: 'workflow',
    description: '获取某条 workflow / subagent playbook 的完整步骤、所需输入、推荐调用和完成标准',
    paramHints: [
      { name: 'workflowId', type: 'string', required: true, description: 'workflow ID，如 sketch_restoration 或 problem_diagnosis' },
    ],
    handler: async (params) => {
      const workflowId = params.workflowId as string;
      if (!workflowId) {
        throw new Error('get_workflow 需要 workflowId 参数');
      }

      const workflow = getWorkflowDetail(workflowId);
      if (!workflow) {
        throw new Error(`未知 workflow: ${workflowId}`);
      }

      return wrapAsContent(workflow);
    },
  },

  {
    name: 'advance_workflow',
    category: 'workflow',
    description: '按已完成步骤、已加载规范和可用输入逐步推进 workflow，返回当前下一步、阻塞点和 fallback 调用建议',
    paramHints: [
      { name: 'workflowId', type: 'string', required: true, description: 'workflow ID，如 sketch_restoration' },
      { name: 'task', type: 'string', description: '原始任务描述，用于辅助框架判断' },
      { name: 'framework', type: 'string', description: '显式框架，如 flutter/vue3/react' },
      { name: 'currentFile', type: 'string', description: '当前文件路径' },
      { name: 'completedSteps', type: 'string[]', description: '已完成 step ID 列表' },
      { name: 'blockedSteps', type: 'string[]', description: '当前认为被阻塞的 step ID 列表' },
      { name: 'availableInputs', type: 'string[]', description: '当前已具备的输入，如 loaderScript/measure-result/projectPath' },
      { name: 'loadedStandards', type: 'string[]', description: '当前已加载的规范 ID 列表' },
    ],
    handler: async (params) => {
      const workflowId = params.workflowId as string;
      if (!workflowId) {
        throw new Error('advance_workflow 需要 workflowId 参数');
      }

      const result = advanceWorkflow({
        workflowId,
        task: params.task as string | undefined,
        framework: params.framework as string | undefined,
        currentFile: params.currentFile as string | undefined,
        completedSteps: params.completedSteps as string[] | undefined,
        blockedSteps: params.blockedSteps as string[] | undefined,
        availableInputs: params.availableInputs as string[] | undefined,
        loadedStandards: params.loadedStandards as string[] | undefined,
      });

      if (!result) {
        throw new Error(`未知 workflow: ${workflowId}`);
      }

      return wrapAsContent(result);
    },
  },

  {
    name: 'validate_workflow',
    category: 'workflow',
    description: '在 create/edit/generate 等关键操作前校验 workflow 是否满足前置条件，返回是否可继续以及缺失项',
    paramHints: [
      { name: 'workflowId', type: 'string', required: true, description: 'workflow ID，如 sketch_restoration' },
      { name: 'operation', type: 'string', required: true, description: '即将执行的操作: create_file/edit_file/multi_edit/generate_code/review/validate/measure' },
      { name: 'task', type: 'string', description: '原始任务描述' },
      { name: 'framework', type: 'string', description: '显式框架，如 flutter/vue3/react' },
      { name: 'currentFile', type: 'string', description: '当前文件路径' },
      { name: 'completedSteps', type: 'string[]', description: '已完成 step ID 列表' },
      { name: 'availableInputs', type: 'string[]', description: '当前已具备的输入，如 measure-result/projectPath' },
      { name: 'loadedStandards', type: 'string[]', description: '当前已加载的规范 ID 列表' },
    ],
    handler: async (params) => {
      const workflowId = params.workflowId as string;
      const operation = params.operation as 'create_file' | 'edit_file' | 'multi_edit' | 'generate_code' | 'review' | 'validate' | 'measure' | undefined;

      if (!workflowId || !operation) {
        throw new Error('validate_workflow 需要 workflowId 和 operation 参数');
      }

      const result = validateWorkflow({
        workflowId,
        operation,
        task: params.task as string | undefined,
        framework: params.framework as string | undefined,
        currentFile: params.currentFile as string | undefined,
        completedSteps: params.completedSteps as string[] | undefined,
        availableInputs: params.availableInputs as string[] | undefined,
        loadedStandards: params.loadedStandards as string[] | undefined,
      });

      if (!result) {
        throw new Error(`未知 workflow: ${workflowId}`);
      }

      return wrapAsContent(result);
    },
  },

  {
    name: 'get_standards',
    category: 'standards',
    description: '智能获取编码规范。支持按项目路径自动分析、按 ID 精确获取、按预设场景获取三种模式',
    paramHints: [
      { name: 'id', type: 'string', description: '规范 ID（如 vue3-composition, flutter）- 按 ID 获取时使用' },
      { name: 'ids', type: 'string[]', description: '批量规范 ID 列表' },
      { name: 'preset', type: 'string', description: '预设场景（如 vue3-component, api-call）- 按场景获取时使用' },
      { name: 'currentFile', type: 'string', description: '当前文件路径 - 智能模式自动分析' },
      { name: 'fileContent', type: 'string', description: '文件内容 - 智能模式自动分析' },
      { name: 'projectPath', type: 'string', description: '项目根路径 - 智能模式自动分析项目技术栈' },
      { name: 'scenario', type: 'string', description: '开发场景' },
      { name: 'customImports', type: 'string[]', description: '预设模式下的额外导入' },
      { name: 'mode', type: 'string', description: '返回模式: summary/key-rules(默认)/full' },
    ],
    handler: async (params) => {
      // 路由逻辑：根据参数自动选择底层实现
      // 优先级：preset > id/ids > 智能模式（currentFile/projectPath）
      if (params.preset) {
        return usePreset({
          preset: params.preset as string,
          customImports: params.customImports as string[] | undefined,
        });
      }
      if (params.id || params.ids) {
        return getStandardById({
          id: params.id as string | undefined,
          ids: params.ids as string[] | undefined,
          mode: params.mode as 'summary' | 'key-rules' | 'full' | undefined,
        });
      }
      // 默认走智能模式
      return getCompactStandards({
        currentFile: params.currentFile as string | undefined,
        fileContent: params.fileContent as string | undefined,
        scenario: params.scenario as string | undefined,
        projectPath: params.projectPath as string | undefined,
        mode: params.mode as 'summary' | 'key-rules' | 'full' | undefined,
      });
    },
  },

  {
    name: 'list_standards',
    category: 'standards',
    description: '列出可用的规范信息。支持列出预设场景、场景列表、场景-规范映射关系',
    paramHints: [
      { name: 'what', type: 'string', description: '查询类型: presets(预设列表) / scenarios(场景列表) / mappings(映射关系)', required: true },
      { name: 'scenario', type: 'string', description: '映射查询：按场景名称' },
      { name: 'fileType', type: 'string', description: '映射查询：按文件类型' },
      { name: 'imports', type: 'string[]', description: '映射查询：按导入包' },
      { name: 'listAll', type: 'boolean', description: '映射查询：列出所有映射' },
    ],
    handler: async (params) => {
      const what = (params.what as string) || 'presets';
      switch (what) {
        case 'presets':
          return listPresets();
        case 'scenarios':
          return listScenarios();
        case 'mappings':
          return queryMappings({
            scenario: params.scenario as string | undefined,
            fileType: params.fileType as string | undefined,
            imports: params.imports as string[] | undefined,
            listAll: params.listAll as boolean | undefined,
          });
        default:
          return listPresets();
      }
    },
  },

  // ==================== 项目分析与配置类 ====================

  {
    name: 'analyze_project',
    category: 'project',
    description: '分析项目技术栈、框架、工具和特征',
    paramHints: [
      { name: 'projectPath', type: 'string', description: '项目绝对路径（可选，默认当前目录）' },
    ],
    handler: async (params) => {
      return analyzeProject({ projectPath: params.projectPath as string | undefined });
    },
  },

  {
    name: 'setup_project',
    category: 'project',
    description: '项目配置管理。支持自动检测并生成配置、快速初始化、生成 copilot-instructions.md',
    paramHints: [
      { name: 'action', type: 'string', description: '操作类型: auto_setup(自动配置) / init(快速初始化) / generate(生成配置)', required: true },
      { name: 'projectPath', type: 'string', description: '项目绝对路径' },
      { name: 'workspacePath', type: 'string', description: '工作区路径（auto_setup 时使用）' },
      { name: 'generateInstructions', type: 'boolean', description: '是否生成 copilot-instructions.md' },
      { name: 'agentIds', type: 'string[]', description: '指定 Agent ID 列表（generate 时使用）' },
      { name: 'autoMatch', type: 'boolean', description: '自动匹配 Agents（generate 时使用）' },
      { name: 'updateMode', type: 'string', description: 'merge(保留自定义) / overwrite(覆盖)' },
      { name: 'configId', type: 'string', description: '配置方案 ID' },
    ],
    handler: async (params) => {
      const action = (params.action as string) || 'auto_setup';
      switch (action) {
        case 'init':
          return initProject({ projectPath: params.projectPath as string | undefined });
        case 'generate': {
          const projectPath = params.projectPath as string;
          if (!projectPath) {
            throw new Error('generate 操作需要 projectPath 参数');
          }
          return generateConfig({
            projectPath,
            agentIds: params.agentIds as string[] | undefined,
            autoMatch: params.autoMatch as boolean | undefined,
            updateMode: params.updateMode as 'merge' | 'overwrite' | undefined,
            configId: params.configId as string | undefined,
          });
        }
        case 'auto_setup':
        default:
          return autoSetup({
            workspacePath: (params.workspacePath || params.projectPath) as string | undefined,
            generateInstructions: params.generateInstructions as boolean | undefined,
          });
      }
    },
  },

  {
    name: 'health_check',
    category: 'project',
    description: '检查 MCP 服务器状态和配置问题，返回诊断报告和修复建议',
    paramHints: [
      { name: 'workspacePath', type: 'string', description: '工作区路径' },
      { name: 'verbose', type: 'boolean', description: '显示详细信息' },
    ],
    handler: async (params) => {
      return healthCheck({
        workspacePath: params.workspacePath as string | undefined,
        verbose: params.verbose as boolean | undefined,
      });
    },
  },

  // ==================== Agent 管理类 ====================

  {
    name: 'manage_agents',
    category: 'agents',
    description: '管理 Copilot Agents。支持列出所有可用 Agent 或按项目特征匹配推荐',
    paramHints: [
      { name: 'action', type: 'string', description: '操作: list(列出全部) / match(按特征匹配)', required: true },
      { name: 'projectFeatures', type: 'object', description: '项目特征（match 时使用，含 frameworks/languages/tools/keywords/projectType）' },
      { name: 'limit', type: 'number', description: '最大返回数量（match 时使用）' },
    ],
    handler: async (params) => {
      const action = (params.action as string) || 'list';
      if (action === 'match' && params.projectFeatures) {
        return matchAgents({
          projectFeatures: params.projectFeatures as {
            frameworks: string[];
            languages: string[];
            tools: string[];
            keywords: string[];
            projectType: string;
          },
          limit: params.limit as number | undefined,
        });
      }
      // 默认列出所有
      return wrapAsContent(listAvailableAgents());
    },
  },

  // ==================== 模板系统类 ====================

  {
    name: 'get_template',
    category: 'templates',
    description: '获取代码模板。支持列出模板列表或获取指定模板详情和文件内容',
    paramHints: [
      { name: 'action', type: 'string', description: '操作: list(列出模板) / get(获取模板详情)', required: true },
      { name: 'id', type: 'string', description: '模板 ID（get 时必填）' },
      { name: 'includeFiles', type: 'boolean', description: '是否包含文件内容' },
      { name: 'files', type: 'string[]', description: '指定要获取的文件列表' },
      { name: 'type', type: 'string', description: '按类型筛选（list 时使用）' },
      { name: 'framework', type: 'string', description: '按框架筛选（list 时使用）' },
      { name: 'search', type: 'string', description: '搜索关键词（list 时使用）' },
    ],
    handler: async (params) => {
      const action = (params.action as string) || 'list';
      if (action === 'get') {
        const id = params.id as string;
        if (!id) {
          throw new Error('get 操作需要 id 参数');
        }
        return getTemplate({
          id,
          includeFiles: params.includeFiles as boolean | undefined,
          files: params.files as string[] | undefined,
        });
      }
      // 默认列出模板
      return listTemplates({
        type: params.type as string | undefined,
        framework: params.framework as string | undefined,
      } as Parameters<typeof listTemplates>[0]);
    },
  },

  // ==================== 项目克隆类 ====================

  {
    name: 'clone_analyze',
    category: 'clone',
    description: '深度分析参考项目的技术栈和架构，用于创建风格一致的新项目',
    paramHints: [
      { name: 'projectPath', type: 'string', required: true, description: '参考项目的绝对路径' },
      { name: 'generateDocs', type: 'boolean', description: '是否生成分析文档' },
      { name: 'newProjectName', type: 'string', description: '新项目名称' },
      { name: 'newProjectPath', type: 'string', description: '新项目路径' },
    ],
    handler: async (params) => {
      const projectPath = params.projectPath as string;
      if (!projectPath) {
        throw new Error('clone_analyze 需要 projectPath 参数');
      }
      return analyzeReferenceProject({
        projectPath,
        generateDocs: params.generateDocs as boolean | undefined,
        newProjectName: params.newProjectName as string | undefined,
        newProjectPath: params.newProjectPath as string | undefined,
      });
    },
  },

  {
    name: 'clone_generate',
    category: 'clone',
    description: '基于参考项目分析结果生成新项目骨架，包含分析文档、开发计划和骨架清单',
    paramHints: [
      { name: 'referenceProjectPath', type: 'string', required: true, description: '参考项目绝对路径' },
      { name: 'newProjectPath', type: 'string', required: true, description: '新项目绝对路径' },
      { name: 'newProjectName', type: 'string', required: true, description: '新项目名称' },
      { name: 'generateDocs', type: 'boolean', description: '是否生成分析文档' },
      { name: 'includeLayers', type: 'string[]', description: '要包含的层级' },
      { name: 'excludePatterns', type: 'string[]', description: '要排除的文件模式' },
    ],
    handler: async (params) => {
      const refPath = params.referenceProjectPath as string;
      const newPath = params.newProjectPath as string;
      const newName = params.newProjectName as string;
      if (!refPath || !newPath || !newName) {
        throw new Error('clone_generate 需要 referenceProjectPath, newProjectPath, newProjectName 参数');
      }
      return generateProjectSkeleton({
        referenceProjectPath: refPath,
        newProjectPath: newPath,
        newProjectName: newName,
        generateDocs: params.generateDocs as boolean | undefined,
        includeLayers: params.includeLayers as string[] | undefined,
        excludePatterns: params.excludePatterns as string[] | undefined,
      });
    },
  },

  // ==================== 问题诊断类 ====================

  {
    name: 'troubleshoot',
    category: 'troubleshoot',
    description: '问题诊断入口。直接传入问题描述，自动识别框架、提取关键词、匹配解决方案',
    paramHints: [
      { name: 'problem', type: 'string', required: true, description: '问题描述（用户说的话或错误信息）' },
      { name: 'framework', type: 'string', description: '框架类型: flutter/vue3/react（可选，会自动检测）' },
      { name: 'codeSnippet', type: 'string', description: '相关代码片段' },
    ],
    handler: async (params) => {
      const problemText = (params.problem as string) || '';

      // 自动检测框架
      let detectedFramework = params.framework as string | undefined;
      if (!detectedFramework) {
        if (problemText.toLowerCase().includes('flutter') ||
            problemText.includes('Widget') ||
            problemText.includes('BoxShadow') ||
            problemText.includes('Dart')) {
          detectedFramework = 'flutter';
        } else if (problemText.toLowerCase().includes('vue') ||
                   problemText.includes('Element') ||
                   problemText.includes('el-')) {
          detectedFramework = 'vue3';
        } else if (problemText.toLowerCase().includes('react')) {
          detectedFramework = 'react';
        }
      }

      const uniqueKeywords = extractTroubleshootingKeywords(problemText);

      return wrapAsContent(await queryTroubleshootingCases({
        framework: detectedFramework,
        keywords: uniqueKeywords.length > 0 ? uniqueKeywords : undefined,
        errorMessage: problemText,
        limit: 5,
      }));
    },
  },

  {
    name: 'query_cases',
    category: 'troubleshoot',
    description: '查询故障排除案例。支持按框架/关键词精确查询、获取单个案例完整内容、列出所有案例',
    paramHints: [
      { name: 'action', type: 'string', description: '操作: query(查询) / get(获取单个) / list(列出全部)', required: true },
      { name: 'framework', type: 'string', description: '框架类型: flutter/vue3/react/common' },
      { name: 'caseId', type: 'string', description: '案例 ID（get 时必填）' },
      { name: 'keywords', type: 'string[]', description: '搜索关键词（query 时使用）' },
      { name: 'errorMessage', type: 'string', description: '错误信息（query 时使用）' },
      { name: 'limit', type: 'number', description: '最大返回数量' },
    ],
    handler: async (params) => {
      const action = (params.action as string) || 'list';
      switch (action) {
        case 'get': {
          const framework = params.framework as string;
          const caseId = params.caseId as string;
          if (!framework || !caseId) {
            throw new Error('get 操作需要 framework 和 caseId 参数');
          }
          return wrapAsContent(await getTroubleshootingCaseContent({ framework, caseId }));
        }
        case 'query':
          return wrapAsContent(await queryTroubleshootingCases({
            framework: params.framework as string | undefined,
            keywords: params.keywords as string[] | undefined,
            errorMessage: params.errorMessage as string | undefined,
            limit: params.limit as number | undefined,
          }));
        case 'list':
        default:
          return wrapAsContent(await listTroubleshootingCases(params.framework as string | undefined));
      }
    },
  },

  // ==================== Sketch 设计稿工具类 ====================

  {
    name: 'sketch_measure',
    category: 'sketch',
    description: '准备 Sketch 测量脚本。脚本写入临时文件，返回轻量加载器代码（~300B），将 loaderScript 传入 mcp_sketch_run_code 执行即可。Sketch 通过 CocoaScript 从磁盘读取完整脚本，不经过 AI 上下文。设计稿还原首轮默认就是 compact(带页面级 restorationContract)，再按需用 measure/style 深挖。支持 compact(紧凑还原合同)、measure(完整画板测量)、layout(布局分析)、style(样式快检)、svg(图标导出)、auto(自动模式)',
    paramHints: [
      { name: 'cmd', type: 'string', description: '执行模式: compact(默认，设计稿还原首轮推荐，输出 restorationContract) / measure(完整画板) / layout(布局分析) / style(样式检查) / svg(图标导出) / auto(自动判断)' },
      { name: 'outputPath', type: 'string', description: 'SVG 输出路径（仅 cmd=svg 时使用）' },
    ],
    handler: async (params) => {
      return sketchMeasure({
        cmd: params.cmd as string | undefined,
        outputPath: params.outputPath as string | undefined,
      });
    },
  },

  {
    name: 'validate_restoration',
    category: 'sketch',
    description: '校验设计还原结果。对照 sketch_measure 的 restorationContract 检查页面骨架、资源来源、真实路由是否接通，以及 UI review 场景是否准备完成',
    paramHints: [
      { name: 'measurePath', type: 'string', description: 'sketch_measure 执行结果文件路径，或保存后的 compact JSON 路径' },
      { name: 'measureContent', type: 'string', description: '直接传入 sketch_measure 的 JSON 文本（measurePath 与 measureContent 二选一）' },
      { name: 'codeFilePath', type: 'string', required: true, description: '生成后的页面代码文件路径' },
      { name: 'routeFilePath', type: 'string', description: '路由文件路径（用于检查是否仍指向占位页）' },
      { name: 'routeName', type: 'string', description: '路由名，如 AppRoutes.recipientAdd' },
      { name: 'projectPath', type: 'string', description: '项目根路径（用于检查 visual review harness）' },
    ],
    handler: async (params) => {
      return wrapAsContent(await validateRestoration({
        measurePath: params.measurePath as string | undefined,
        measureContent: params.measureContent as string | undefined,
        codeFilePath: params.codeFilePath as string | undefined,
        routeFilePath: params.routeFilePath as string | undefined,
        routeName: params.routeName as string | undefined,
        projectPath: params.projectPath as string | undefined,
      }));
    },
  },
];
